import React, { useReducer, useState, useEffect } from "react";
import {
  singleRoleRouletteReducer,
  initialSingleRoleRouletteState,
  GameMode,
} from "./SingleRoleRouletteReducer";
import SingleRoleRouletteGameSetting from "./SingleRoleRouletteGameSetting";
import ProductGameHeader from "../ProductGameHeader";
import { useTranslation } from "react-i18next";
import { Product } from "../../module/types";
import "sass/views/roleDistributor/RoleDistributorView.scss";
import { useNavigate } from "react-router-dom";

interface Props {
  product: Product;
}

/**
 * SingleRoleRoulette 메인 게임 뷰
 * - 1인용, 서버 통신 없음
 * - 룰렛 모드: 한 명씩 역할 확인
 * - 사회자 모드: 전체 결과 보기
 */
const SingleRoleRouletteView: React.FC<Props> = ({ product }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(
    singleRoleRouletteReducer,
    initialSingleRoleRouletteState
  );
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [isRoleVisible, setIsRoleVisible] = useState(false);
  const [rouletteStage, setRouletteStage] = useState<
    "playing" | "waiting" | "results"
  >("playing");
  const [settingError, setSettingError] = useState("");
  const navigate = useNavigate();

  // 자주 사용되는 state 값들
  const {
    labels: customLabels,
    defaultLabel,
    totalPlayers,
    gameMode,
    settingsCompleted,
    started,
    assignments,
    currentPlayerIndex,
    simpleVoteSession,
  } = state;

  // 비공개 찬반투표 UI 상태
  const [isVoterCountModalOpen, setIsVoterCountModalOpen] = useState(false);
  const [voterCountInput, setVoterCountInput] = useState(totalPlayers);
  const [selectedVote, setSelectedVote] = useState<"agree" | "disagree" | null>(
    null
  );

  // 계산된 값들
  const totalCustomCount = customLabels.reduce((sum, l) => sum + l.count, 0);
  const defaultCount = totalPlayers - totalCustomCount;
  const currentPlayer = assignments[currentPlayerIndex];

  // 플레이어 인덱스가 변경될 때마다 역할 숨김
  useEffect(() => {
    setIsRoleVisible(false);
  }, [currentPlayerIndex]);

  // 설정이 완료되지 않은 상태로 돌아가면 룰렛 단계 초기화
  useEffect(() => {
    if (!settingsCompleted) {
      setRouletteStage("playing");
    }
  }, [settingsCompleted]);

  // ==================== EVENT HANDLERS ====================

  // 설정 완료 핸들러
  const handleCompleteSettings = () => {
    // 1. 기본 역할 검증
    if (!state.defaultLabel?.name || !state.defaultLabel.name.trim()) {
      setSettingError(t("SingleRoleRoulette.enterBasicRole"));
      return;
    }

    // 2. 총 인원 수 검증
    if (state.totalPlayers < 1) {
      setSettingError(t("SingleRoleRoulette.setTotalPlayersMinimum"));
      return;
    }

    // 3. 특수역할 인원 수 총합 검증
    const totalLabelCount = state.labels.reduce((sum, l) => sum + l.count, 0);
    if (totalLabelCount > state.totalPlayers) {
      setSettingError(
        t("SingleRoleRoulette.totalLabelCountExceedsTotalPlayers")
      );
      return;
    }

    dispatch({ type: "SET_SETTINGS_COMPLETED", completed: true });
    setSettingError("");
  };

  // 게임 시작 핸들러
  const handleStart = () => {
    dispatch({ type: "START_GAME" });
    setSettingError("");
  };

  // 설정 수정 핸들러
  const handleResetSettings = () => {
    dispatch({ type: "SET_SETTINGS_COMPLETED", completed: false });
    setSettingError("");
  };

  // 분배 종료 핸들러 (역할 설정 유지)
  const handleRestart = () => {
    dispatch({ type: "END_DISTRIBUTION" });
    setSettingError("");
    setRouletteStage("playing");
    setIsRoleVisible(false);
  };

  const handleConfirmRole = () => {
    if (currentPlayerIndex === assignments.length - 1) {
      // 마지막 플레이어
      setRouletteStage("waiting");
    } else {
      dispatch({ type: "NEXT_PLAYER" });
    }
  };

  const handleNextPlayer = () => {
    dispatch({ type: "NEXT_PLAYER" });
  };

  const handlePreviousPlayer = () => {
    dispatch({ type: "PREVIOUS_PLAYER" });
  };

  const handlePlayerSelect = (index: number) => {
    dispatch({ type: "SET_CURRENT_PLAYER", index });
  };

  const handleLeaveRoom = () => {
    navigate("/", { replace: true });
  };

  // 비공개 찬반투표 핸들러
  const handleOpenVoterCountModal = () => {
    setVoterCountInput(totalPlayers);
    setIsVoterCountModalOpen(true);
  };

  const handleStartSimpleVote = () => {
    dispatch({ type: "START_SIMPLE_VOTE", totalVoters: voterCountInput });
    setIsVoterCountModalOpen(false);
  };

  const handleConfirmVote = () => {
    if (!simpleVoteSession || !selectedVote) return;
    const playerNumber = simpleVoteSession.currentVoterIndex + 1;
    dispatch({ type: "CAST_SIMPLE_VOTE", playerNumber, choice: selectedVote });

    // 선택 초기화
    setSelectedVote(null);

    // 마지막 투표자가 아니면 다음 투표자로
    if (
      simpleVoteSession.currentVoterIndex <
      simpleVoteSession.totalVoters - 1
    ) {
      dispatch({ type: "NEXT_SIMPLE_VOTER" });
    } else {
      // 마지막 투표자면 결과 계산
      dispatch({ type: "SHOW_SIMPLE_VOTE_RESULTS" });
    }
  };

  const handleEndSimpleVote = () => {
    dispatch({ type: "END_SIMPLE_VOTE" });
    setRouletteStage("waiting");
  };

  // ==================== RENDER FUNCTIONS ====================

  // 헤더 렌더링
  const renderHeader = () => (
    <ProductGameHeader
      product={product}
      isRuleOpen={isRuleOpen}
      setIsRuleOpen={setIsRuleOpen}
      isSettingOpen={isSettingOpen}
      setIsSettingOpen={setIsSettingOpen}
      t={t}
      extraHeaderButtons={
        started ? (
          <button
            onClick={handleRestart}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white rounded-lg shadow-lg transition-all duration-200 font-medium flex-shrink-0 will-change-transform"
            style={{ minWidth: "fit-content", minHeight: "32px" }}
          >
            <i className="fas fa-stop text-xs" />
            <span>{t("RoleDistributor.endDistribution")}</span>
          </button>
        ) : null
      }
    />
  );

  const renderSettingMenu = () =>
    isSettingOpen && (
      <div className="fixed right-0 top-[52px] mt-2 w-40 bg-gray-800 rounded-lg shadow-xl z-40 border border-gray-700">
        <ul className="py-1">
          <li
            className="px-4 py-2 hover:bg-gray-700 cursor-pointer text-red-400 hover:text-white transition-colors"
            onClick={handleLeaveRoom}
          >
            <i className="fas fa-sign-out-alt mr-2" />
            {t("leaveRoom")}
          </li>
        </ul>
      </div>
    );

  // 요약 상황판 렌더링
  const renderSummaryPanel = () => {
    if (!settingsCompleted) return null;

    return (
      <div className="bg-gray-800/50 border-b border-gray-700 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          {/* 공통 정보 영역 */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {/* 게임 상태 */}
            <div
              className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                started
                  ? "bg-green-600/20 text-green-300"
                  : "bg-gray-600/20 text-gray-300"
              }`}
            >
              <i className={`fas ${started ? "fa-play" : "fa-pause"} mr-1`} />
              {started
                ? t("SingleRoleRoulette.inProgress")
                : t("SingleRoleRoulette.waiting")}
            </div>

            {/* 게임 모드 */}
            <div className="text-xs px-2 py-1 bg-purple-600/20 text-purple-300 rounded-full whitespace-nowrap">
              <i className="fas fa-gamepad mr-1" />
              {gameMode === GameMode.ROULETTE
                ? t("SingleRoleRoulette.rouletteMode")
                : t("SingleRoleRoulette.moderatorMode")}
            </div>

            {/* 총 인원 */}
            <div className="text-xs px-2 py-1 bg-blue-600/20 text-blue-300 rounded-full whitespace-nowrap">
              <i className="fas fa-users mr-1" />
              {t("SingleRoleRoulette.totalPlayers")} {totalPlayers}
              {t("SingleRoleRoulette.people")}
            </div>
          </div>

          {/* 역할 정보 영역 */}
          <div className="flex flex-wrap items-center gap-2">
            {/* 기본 역할 */}
            <div className="flex items-center bg-blue-900/20 border border-blue-700/30 rounded-lg overflow-hidden whitespace-nowrap">
              {defaultCount > 0 && (
                <span className="bg-blue-600 text-white text-xs px-2 py-1 font-medium">
                  {defaultCount}
                </span>
              )}
              <span className="text-blue-300 text-sm px-3 py-1">
                {defaultLabel?.name || t("SingleRoleRoulette.notSet")}
              </span>
            </div>

            {/* 특수 역할들 */}
            {customLabels.map((label) => (
              <div
                key={label.name}
                className="flex items-center bg-purple-900/20 border border-purple-700/30 rounded-lg overflow-hidden whitespace-nowrap"
              >
                <span className="bg-purple-600 text-white text-xs px-2 py-1 font-medium">
                  {label.count}
                </span>
                <span className="text-purple-300 text-sm px-3 py-1">
                  {label.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // 역할 카드 컴포넌트 (기본 역할 vs 특수 역할 색상 분기)
  const RoleCard: React.FC<{
    playerNumber: number;
    role: string;
    isSpecialRole: boolean;
    onConfirm: () => void;
  }> = ({ playerNumber, role, isSpecialRole, onConfirm }) => {
    const colors = isSpecialRole
      ? {
          iconGradient: "from-purple-500 to-pink-600",
          badgeGradient: "from-purple-600 to-pink-600",
          bgGradient: "from-purple-900/20 to-pink-900/20",
        }
      : {
          iconGradient: "from-blue-500 to-indigo-600",
          badgeGradient: "from-blue-600 to-indigo-600",
          bgGradient: "from-blue-900/20 to-indigo-900/20",
        };

    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div
          className={`rounded-3xl bg-gradient-to-br ${colors.bgGradient} border border-gray-600 text-center max-w-2xl w-full flex flex-col justify-center`}
        >
          {/* 플레이어 번호 */}
          <div className="text-gray-300 text-2xl my-4">
            {t("SingleRoleRoulette.player")} {playerNumber}
          </div>

          {/* 역할 아이콘 */}
          <div
            className={`w-16 h-16 bg-gradient-to-br ${colors.iconGradient} rounded-full flex items-center justify-center mx-auto mb-8`}
          >
            <i className="fas fa-user-secret text-white text-4xl" />
          </div>

          {/* 역할 이름 */}
          <div
            className={`mx-auto min-w-60 px-8 py-2 bg-gradient-to-r ${colors.badgeGradient} text-white rounded-xl font-bold text-2xl`}
          >
            {role}
          </div>

          <div className="text-gray-400 py-4 text-center">
            {t("SingleRoleRoulette.passToNextPlayerAfterCheck")}
          </div>
          {/* 확인 완료 버튼 */}
          <button
            onClick={onConfirm}
            className="mx-auto px-8 py-4 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white rounded-xl font-bold text-2xl mb-8 transition-all"
          >
            {t("SingleRoleRoulette.confirmComplete")}
          </button>
        </div>
      </div>
    );
  };

  // 룰렛 모드 - 현재 플레이어 역할 표시
  const renderRouletteMode = () => {
    if (!currentPlayer) return null;

    // 1. 가려진 화면
    if (!isRoleVisible) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <div className="py-4 rounded-3xl bg-gray-800/50 border border-gray-600 text-center max-w-2xl w-full flex flex-col justify-center">
            {/* 플레이어 번호 */}
            <div className="text-gray-300 text-2xl mb-6">
              {t("SingleRoleRoulette.player")} {currentPlayer.playerNumber}
            </div>

            {/* 역할 아이콘 (숨김) */}
            <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-8">
              <i className="fas fa-question text-white text-4xl" />
            </div>

            {/* 확인하기 버튼 */}
            <button
              onClick={() => setIsRoleVisible(true)}
              className="mx-auto px-16 py-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl font-bold text-2xl transition-all"
            >
              {t("SingleRoleRoulette.checkRole")}
            </button>

            {/* 네비게이션 숫자 */}
            <div className="text-gray-400 text-2xl font-semibold mt-8">
              {currentPlayerIndex + 1} / {assignments.length}
            </div>
          </div>
        </div>
      );
    }

    // 2. 역할 확인 화면 - 공통 컴포넌트 사용
    const isSpecialRole = currentPlayer.role !== defaultLabel?.name;

    return (
      <RoleCard
        playerNumber={currentPlayer.playerNumber}
        role={currentPlayer.role}
        isSpecialRole={isSpecialRole}
        onConfirm={handleConfirmRole}
      />
    );
  };

  // 대기 화면 렌더링
  const renderWaitingForResults = () => {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center gap-16">
        <div className="flex flex-col items-center">
          <i className="fas fa-check-circle text-6xl text-green-400 mb-6" />
          <h2 className="text-2xl font-bold text-white mb-12">
            {t("SingleRoleRoulette.allPlayersConfirmed")}
          </h2>

          {/* 비공개 찬반투표 버튼 */}
          <button
            onClick={handleOpenVoterCountModal}
            className="w-72 max-w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-bold text-xl transition-all"
          >
            🔒 {t("SingleRoleRoulette.secretVote")}
          </button>
        </div>

        {/* 결과 확인하기 버튼 */}
        <button
          onClick={() => setRouletteStage("results")}
          className="w-72 max-w-full px-6 py-3 bg-gradient-to-r from-blue-800 via-teal-700/80 to-cyan-800 hover:from-blue-900 hover:via-teal-800 hover:to-cyan-900 text-white rounded-xl font-bold text-xl transition-all border border-teal-600/40"
        >
          {t("SingleRoleRoulette.viewResults")}
        </button>
      </div>
    );
  };

  // 투표 인원 선택 모달
  const renderVoterCountModal = () => {
    if (!isVoterCountModalOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full border border-gray-600">
          <h3 className="text-2xl font-bold text-white mb-4 text-center">
            {t("SingleRoleRoulette.selectVoterCount")}
          </h3>

          {/* 기능 설명 */}
          <p className="text-gray-400 text-sm mb-6 text-center leading-relaxed">
            {t("SingleRoleRoulette.secretVoteDescription")}
          </p>

          <div className="flex items-center justify-center gap-6 mb-8">
            <button
              onClick={() =>
                setVoterCountInput(Math.max(1, voterCountInput - 1))
              }
              className="w-14 h-14 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white rounded-full font-bold text-2xl transition-all flex items-center justify-center"
            >
              -
            </button>

            <div className="text-5xl font-bold text-white min-w-[80px] text-center">
              {voterCountInput}
            </div>

            <button
              onClick={() =>
                setVoterCountInput(Math.min(totalPlayers, voterCountInput + 1))
              }
              className="w-14 h-14 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-full font-bold text-2xl transition-all flex items-center justify-center"
            >
              +
            </button>
          </div>

          <p className="text-gray-400 text-center mb-6">
            {t("SingleRoleRoulette.totalPlayers")}: {totalPlayers}
            {t("SingleRoleRoulette.people")}
          </p>

          <div className="flex gap-3">
            <button
              onClick={() => setIsVoterCountModalOpen(false)}
              className="flex-1 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-bold transition-all"
            >
              {t("RoleDistributor.cancel")}
            </button>
            <button
              onClick={handleStartSimpleVote}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-bold transition-all"
            >
              {t("RoleDistributor.start")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 투표 진행 화면
  const renderSimpleVoting = () => {
    if (!simpleVoteSession || simpleVoteSession.completed) return null;

    const currentVoterNumber = simpleVoteSession.currentVoterIndex + 1;

    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 md:p-8 text-center">
        <div className="mb-4 md:mb-6">
          <div className="text-gray-400 text-sm md:text-lg mb-2">
            {t("SingleRoleRoulette.votingInProgress")}
          </div>
          <div className="text-2xl md:text-4xl font-bold text-white">
            {currentVoterNumber} / {simpleVoteSession.totalVoters}
          </div>
        </div>

        <h2 className="text-xl md:text-3xl font-bold text-white mb-6 md:mb-12">
          {t("SingleRoleRoulette.player")} {currentVoterNumber}
        </h2>

        <div className="flex gap-4 md:gap-12 w-full max-w-md md:max-w-2xl mx-auto mb-8">
          <button
            onClick={() => setSelectedVote("agree")}
            className={`flex-1 px-8 py-6 md:px-16 md:py-8 rounded-2xl font-bold text-xl md:text-3xl transition-all flex flex-col items-center gap-2 md:gap-3 ${
              selectedVote === "agree"
                ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white ring-4 ring-green-400"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            <div className="text-4xl md:text-6xl">👍</div>
            <div>{t("SingleRoleRoulette.agree")}</div>
          </button>

          <button
            onClick={() => setSelectedVote("disagree")}
            className={`flex-1 px-8 py-6 md:px-16 md:py-8 rounded-2xl font-bold text-xl md:text-3xl transition-all flex flex-col items-center gap-2 md:gap-3 ${
              selectedVote === "disagree"
                ? "bg-gradient-to-r from-red-600 to-pink-600 text-white ring-4 ring-red-400"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            <div className="text-4xl md:text-6xl">👎</div>
            <div>{t("SingleRoleRoulette.disagree")}</div>
          </button>
        </div>

        <button
          onClick={handleConfirmVote}
          disabled={!selectedVote}
          className={`px-8 py-4 rounded-xl font-bold text-xl transition-all ${
            selectedVote
              ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              : "bg-gray-700 text-gray-500 cursor-not-allowed"
          }`}
        >
          {t("RoleDistributor.confirm")}
        </button>
      </div>
    );
  };

  // 투표 결과 대기 화면
  const renderSimpleVoteResultsWaiting = () => {
    if (
      !simpleVoteSession ||
      !simpleVoteSession.completed ||
      !simpleVoteSession.results
    )
      return null;

    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <i className="fas fa-check-circle text-6xl text-green-400 mb-6" />
        <h2 className="text-2xl font-bold text-white mb-4">
          {t("SingleRoleRoulette.allVotesComplete")}
        </h2>
        <p className="text-gray-400 mb-8">
          {t("SingleRoleRoulette.confirmVoteResults")}
        </p>
        <button
          onClick={() => setRouletteStage("results")}
          className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-bold text-2xl transition-all"
        >
          {t("SingleRoleRoulette.voteResults")}
        </button>
      </div>
    );
  };

  // 투표 결과 표시 화면
  const renderSimpleVoteResults = () => {
    if (!simpleVoteSession?.results) return null;

    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            {t("SingleRoleRoulette.voteResults")}
          </h2>

          <div className="space-y-4 mb-8">
            <div className="flex items-center justify-between p-4 bg-green-900/20 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="text-4xl">👍</div>
                <div className="text-xl font-bold text-green-300">
                  {t("SingleRoleRoulette.agree")}
                </div>
              </div>
              <div className="text-4xl font-bold text-green-300">
                {simpleVoteSession.results.agree}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-red-900/20 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="text-4xl">👎</div>
                <div className="text-xl font-bold text-red-300">
                  {t("SingleRoleRoulette.disagree")}
                </div>
              </div>
              <div className="text-4xl font-bold text-red-300">
                {simpleVoteSession.results.disagree}
              </div>
            </div>
          </div>

          <button
            onClick={handleEndSimpleVote}
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-bold text-lg transition-all"
          >
            {t("SingleRoleRoulette.complete")}
          </button>
        </div>
      </div>
    );
  };

  // 사회자 모드 - 전체 결과 표시
  const renderModeratorMode = () => {
    const getRoleColors = (role: string) => {
      if (role === defaultLabel?.name) {
        return {
          bg: "bg-blue-900/20 border-blue-700/30",
          badge: "bg-blue-600 text-blue-50",
        };
      } else {
        return {
          bg: "bg-purple-900/20 border-purple-700/30",
          badge: "bg-purple-600 text-purple-50",
        };
      }
    };

    return (
      <div className="flex-1 p-6">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-white mb-8 text-center">
            {t("SingleRoleRoulette.roleDistributionResults")}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {assignments.map((assignment) => {
              const colors = getRoleColors(assignment.role);
              return (
                <div
                  key={assignment.playerNumber}
                  className={`flex items-center justify-between px-3 py-2 md:px-6 md:py-4 ${colors.bg} border rounded-xl`}
                >
                  <span className="text-white font-medium text-sm md:text-base">
                    {t("SingleRoleRoulette.player")} {assignment.playerNumber}
                  </span>
                  <span
                    className={`px-2 py-1 md:px-3 ${colors.badge} rounded-lg text-xs md:text-sm font-medium`}
                  >
                    {assignment.role}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // 게임 설정 영역
  const renderGameSettings = () => {
    return (
      <div className="p-6 flex-1">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-800 via-teal-700/80 to-cyan-800 rounded-lg flex items-center justify-center mr-3 border border-teal-600/40">
                  <i className="fas fa-cog text-white text-sm" />
                </div>
                <h2 className="text-lg font-bold text-white">
                  {t("RoleDistributor.roleSettingsHost")}
                </h2>
              </div>
              <div className="flex gap-2">
                {/* 설정 완료 전: 설정 완료 버튼 */}
                {!state.settingsCompleted && (
                  <button
                    onClick={handleCompleteSettings}
                    className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-medium text-xs transition-colors flex items-center gap-1"
                  >
                    <i className="fas fa-check text-xs" />
                    {t("RoleDistributor.completeSettings")}
                  </button>
                )}

                {/* 설정 완료 후: 수정하기/시작하기 또는 재시작 버튼 */}
                {state.settingsCompleted && (
                  <div>
                    {state.started ? (
                      <button
                        onClick={handleRestart}
                        className="px-3 py-1.5 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white rounded-lg font-medium text-xs transition-colors flex items-center gap-1"
                      >
                        <i className="fas fa-redo text-xs" />
                        {t("LuckyDraw.restart")}
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={handleResetSettings}
                          className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white rounded-lg font-medium text-xs transition-colors flex items-center gap-1"
                        >
                          <i className="fas fa-edit text-xs" />
                          {t("RoleDistributor.edit")}
                        </button>
                        <button
                          onClick={handleStart}
                          className="px-3 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-medium text-xs transition-colors flex items-center gap-1"
                        >
                          <i className="fas fa-play text-xs" />
                          {t("RoleDistributor.start")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <SingleRoleRouletteGameSetting
              state={state}
              dispatch={dispatch}
              error={settingError}
              setError={setSettingError}
            />
          </div>
        </div>
      </div>
    );
  };

  // 결과 표시 영역
  const renderResults = () => {
    return (
      <div className="flex-1 flex flex-col">
        {/* 설정 화면 - 설정이 완료되지 않았거나 시작되지 않은 경우 */}
        {(!settingsCompleted || !started) && renderGameSettings()}

        {/* 게임이 시작된 상태 */}
        {started && (
          <div>
            {/* 투표가 진행 중일 때 */}
            {simpleVoteSession &&
              !simpleVoteSession.completed &&
              renderSimpleVoting()}

            {/* 투표가 완료되었지만 결과를 보지 않았을 때 */}
            {simpleVoteSession &&
              simpleVoteSession.completed &&
              rouletteStage === "waiting" &&
              renderSimpleVoteResultsWaiting()}

            {/* 투표 결과 화면 */}
            {simpleVoteSession &&
              simpleVoteSession.completed &&
              rouletteStage === "results" &&
              renderSimpleVoteResults()}

            {/* 투표가 없을 때의 기본 화면들 */}
            {!simpleVoteSession && (
              <>
                {/* 사회자 모드 */}
                {gameMode === GameMode.MODERATOR && renderModeratorMode()}

                {/* 룰렛 모드 - 플레이 중 */}
                {gameMode === GameMode.ROULETTE &&
                  rouletteStage === "playing" &&
                  renderRouletteMode()}

                {/* 룰렛 모드 - 대기 화면 */}
                {gameMode === GameMode.ROULETTE &&
                  rouletteStage === "waiting" &&
                  renderWaitingForResults()}

                {/* 룰렛 모드 - 결과 화면 */}
                {gameMode === GameMode.ROULETTE &&
                  rouletteStage === "results" &&
                  renderModeratorMode()}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // 메인 컨텐츠 렌더링
  const renderMainContent = () => (
    <div className="single-role-roulette-main flex-1 flex flex-col min-h-0 pt-[56px] overflow-y-auto">
      {renderSummaryPanel()}
      {renderResults()}
    </div>
  );

  // ==================== MAIN RENDER ====================

  return (
    <div className="space-gray-theme single-role-roulette-container flex flex-col h-screen">
      {renderHeader()}
      {renderSettingMenu()}
      {renderMainContent()}
      {renderVoterCountModal()}
    </div>
  );
};

export default SingleRoleRouletteView;
